"use client";

import React from "react";

import DecorationLine from "@/components/global/ui/DecorationLine";
import CornerChip from "@/components/global/ui/CornerChip";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import Chip from "@/components/global/ui/Chip";
import {CTACornerButton} from "@/shadcn/ui/corner-button";
import {AnimatedAsset1} from "@/components/global/animatedAssets/Asset1";
import {AnimatedAsset4} from "@/components/global/animatedAssets/Asset4";
import {AnimatedAsset3} from "@/components/global/animatedAssets/Asset3";
import {AnimatedAsset5} from "@/components/global/animatedAssets/Asset5";

import {PageHomeNewSchema} from "@/sanity/pages/PageHomeNewTemplate";

const animationMap = [AnimatedAsset1, AnimatedAsset4, AnimatedAsset3, AnimatedAsset5];

export default function TradingSection({
  data,
  className
}: {
  data: PageHomeNewSchema;
  className?: string;
}) {

  return (
    <section className={cn(
      "relative overflow-clip pt-[10rem] lg:pt-[14.5rem] pb-[2rem] lg:pb-[16rem]",
      className
    )}>
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />
      <DecorationLine
        className="left-0 top-0"
        orientation="horizontal"
      />

      <DecorationSquares
        className="top-[2px]"
      />

      <div className="container md:max-w-[112rem] lg:max-w-[216.5rem] flex flex-col items-center relative">
        <Chip
          image={data?.tradingChip?.icon}
          text={data?.tradingChip?.text}
        />

        {data?.tradingTitle && (
          <div className="richtext w-[min(70rem,100%)] lg:w-[min(95rem,100%)]">
            <PortableText
              value={data.tradingTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.tradingCTA && (
          <CTACornerButton
            className="mt-[3rem]"
            data={data.tradingCTA}
          />
        )}

        {data?.tradingItems && (
          <div className="flex flex-col items-center md:items-stretch md:grid md:grid-cols-2 lg:grid-cols-4 gap-[2rem] mt-[3rem] lg:mt-[6rem]">
            {data?.tradingItems.map((item, index) => {
              const AnimatedAsset = animationMap[index] || null;

              return (
                <div
                  key={index}
                  className="bg-white w-[min(57.5rem,100%)] md:w-full"
                 >
                  {AnimatedAsset && (
                    <div className="h-[40rem] lg:h-[50rem] overflow-clip relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]">
                      <div className="absolute top-1/2 -translate-y-1/2 w-full">
                        <AnimatedAsset language={data?.settings?.language || "de"} />
                      </div>
                    </div>
                  )}

                  <div className="relative p-[3rem] sm:p-[4rem]">
                    <CornerChip
                      className="absolute -left-[1px] -translate-y-px bottom-full"
                      image={item?.chip?.icon}
                      text={item?.chip?.text}
                    />

                    {item?.text && (
                      <div className="richtext richtext-start prose-p:text-black/80 prose-strong:font-medium prose-strong:text-black">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
